/**
 * Withdrawal Method Selector
 * 
 * Handles payment method selection and toggles between card form and iframe
 * 
 * @package betbeast
 */

export class WithdrawalMethodSelector {
    constructor(modalElement, currencyDisplayManager = null, schemaRenderer = null) {
        this.modal = modalElement;
        this.selectedMethod = 'bank'; // Default to bank/card (API code)
        this.selectedVariantKey = null; // Identifies the selected card among same-code variants
        this.cardPaymentMethods = ['bank']; // Methods that use card form
        this.currencyDisplayManager = currencyDisplayManager;
        this.schemaRenderer = schemaRenderer;
        
        if (!this.modal) {
            console.warn('Modal element not provided to WithdrawalMethodSelector');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize payment method selection listeners
     */
    init() {
        const paymentMethodButtons = this.modal.querySelectorAll('.wallet-withdrawal-payment-method-item');
        
        paymentMethodButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                this.selectPaymentMethod(button.getAttribute('data-variant-key'), button);
            });
        });

        // Select first available withdrawal method by default
        if (paymentMethodButtons.length > 0) {
            const firstButton = paymentMethodButtons[0];
            this.selectPaymentMethod(firstButton.getAttribute('data-variant-key'), firstButton);
        }
    }
    
    /**
     * Select a payment method
     * @param {string} method - Payment method identifier (visa, revolut, bank, etc.)
     */
    selectPaymentMethod(variantKey, buttonElement = null) {
        if (!variantKey) return;

        // Resolve the selected card. Several cards can share an API code (variants),
        // so the variant key — not the code — identifies a single card.
        const button = buttonElement ?? this.modal.querySelector(
            `.wallet-withdrawal-payment-method-item[data-variant-key="${variantKey}"]`
        );

        // The API code is shared across variants and drives limits + submission.
        const method = button ? button.getAttribute('data-payment-method') : variantKey;

        this.selectedVariantKey = variantKey;
        this.selectedMethod = method;
        // Keep the selected variant button so saved-entry selection can re-render
        // this method's schema form when the player chooses "use a new …".
        this.activeButton = button;

        // Clear any "deposit first" lock left by a previously selected method.
        this.setWithdrawalDisabled(false);

        // Update button active states
        this.updateButtonStates(variantKey);

        // Update min/max limits from selected button
        this.updateLimitsFromButton(button);

        // Swap the fine print under the security badges for this card's notice.
        this.updateLegalNotice(button);

        // Toggle between card form and iframe
        this.togglePaymentDisplay(method);

        // Whether this method should fetch + display saved payment info
        // (configured per method in WP admin).
        const showSaved = button?.getAttribute('data-show-saved-info') === 'true';

        if (showSaved) {
            // Saved-entry selection drives whether the schema form is shown, so
            // clear it now and let fetchPaymentInfo() decide once data arrives.
            if (this.schemaRenderer) this.schemaRenderer.renderForMethod(null);
            this.fetchPaymentInfo(method);
        } else {
            // No saved info for this method — hide the section and render the
            // schema-defined extra fields as the only way to enter details.
            const savedCardsSection = this.modal.querySelector('[data-saved-cards]');
            if (savedCardsSection) savedCardsSection.style.display = 'none';
            if (this.schemaRenderer) this.schemaRenderer.renderForMethod(button);
        }
    }
    
    /**
     * Fetch payment info for selected payment method
     * @param {string} method - Payment method identifier (API code like APCOPAY_V2_CARD)
     */
    async fetchPaymentInfo(method) {
        // The method parameter already contains the API code (data-payment-method).
        const apiCode = method;

        // Payment type is used as the API filter. May be null for codes not in the
        // map; the endpoint still filters by paymentMethods, and each returned
        // record carries its own `type` used for rendering.
        const paymentType = this.getPaymentTypeFromApiCode(apiCode);

        // The variant suffix (SEPA/OCT/ACH/ETO) after "{apiCode}_" equals the saved
        // instrument's PAYOUT_TYPE; when present it narrows the list to this variant.
        // Bare codes (no suffix) send nothing → the API returns the full list.
        const payoutType =
            this.selectedVariantKey && this.selectedVariantKey.startsWith(apiCode + '_')
                ? this.selectedVariantKey.slice(apiCode.length + 1).toUpperCase()
                : '';

        // Show the saved section in a loading state while we fetch. Clear any
        // leftover "deposit first" message / hidden title from the previous method.
        const savedCardsSection = this.modal.querySelector('[data-saved-cards]');
        const loadingEl         = this.modal.querySelector('[data-saved-cards-loading]');
        const cardsList         = this.modal.querySelector('[data-saved-cards-list]');
        const emptyNote         = this.modal.querySelector('[data-saved-cards-empty]');
        const depositNote       = this.modal.querySelector('[data-saved-cards-deposit-note]');
        const titleEl           = this.modal.querySelector('[data-saved-cards-title]');
        if (savedCardsSection) {
            savedCardsSection.style.display = 'block';
            if (loadingEl)   loadingEl.style.display   = 'flex';
            if (cardsList)   cardsList.style.display   = 'none';
            if (emptyNote)   emptyNote.style.display   = 'none';
            if (depositNote) depositNote.style.display = 'none';
            if (titleEl)     titleEl.style.display     = '';
        }

        try {
            const formData = new FormData();
            formData.append('action', 'get_payment_info');
            formData.append('payment_method', apiCode);
            if (paymentType) {
                formData.append('type', paymentType);
            }
            if (payoutType) {
                formData.append('payout_type', payoutType);
            }
            formData.append('nonce', window.accountData?.nonce || '');

            const response = await fetch(window.accountData?.ajaxUrl || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();

            if (result.success && result.data) {
                this.updatePaymentInfoDisplay(result.data.data, paymentType);
            } else {
                // API error — fall back to manual entry via the schema form.
                console.error('Failed to load payment info:', result.data?.message);
                this.updatePaymentInfoDisplay([], paymentType);
            }
        } catch (error) {
            // Network failure — hide the section and let the player enter new details.
            console.error('Error fetching payment info:', error);
            if (loadingEl)         loadingEl.style.display = 'none';
            if (savedCardsSection) savedCardsSection.style.display = 'none';
            this.showSchemaForNewEntry();
        }
    }
    
    /**
     * Get payment type from API code
     * @param {string} apiCode - API code (e.g., APCOPAY_V2_CARD, APCOPAY_V2_INTERAC)
     * @returns {string|null} Payment type (BANK, CREDIT_CARD, CRYPTO_ADDRESS, PAYMENT_WALLET)
     */
    getPaymentTypeFromApiCode(apiCode) {
        // Map API codes to payment types
        const typeMap = {
            'APCOPAY_V2_CARD': 'CREDIT_CARD',
            'APCOPAY_V2_INTERAC': 'BANK',
            'APCOPAY_V2_CONNPAY': 'BANK',
            'APCOPAY_V2_COINPAYMENTS': 'CRYPTO_ADDRESS',
            'APCOPAY_V2_CRYPTOPAY': 'CRYPTO_ADDRESS',
            'APCOPAY_V2_SEKOPENBANKING': 'BANK',
            'APCOPAY_V2_CASHLIB': 'PAYMENT_WALLET',
            'APCOPAY_V2_MIFINITY': 'PAYMENT_WALLET',
            'APCOPAY_V2_SKRILL': 'PAYMENT_WALLET',
            'APCOPAY_V2_NETELLER': 'PAYMENT_WALLET',
        };
        
        return typeMap[apiCode] || null;
    }
    
    /**
     * Get payment type based on method (legacy method, kept for compatibility)
     * @param {string} method - Payment method identifier
     * @returns {string|null} Payment type (BANK, CREDIT_CARD, CRYPTO_ADDRESS, PAYMENT_WALLET)
     */
    getPaymentType(method) {
        // Map UI method identifiers to API payment types
        const typeMap = {
            'bank': 'BANK',
            'card': 'CREDIT_CARD',
            'visa': 'CREDIT_CARD',
            'mastercard': 'CREDIT_CARD',
            'crypto': 'CRYPTO_ADDRESS',
            'bitcoin': 'CRYPTO_ADDRESS',
            'ethereum': 'CRYPTO_ADDRESS',
            'mifinity': 'PAYMENT_WALLET',
            'skrill': 'PAYMENT_WALLET',
            'neteller': 'PAYMENT_WALLET',
            'interac': 'BANK',
        };
        
        return typeMap[method.toLowerCase()] || null;
    }
    
    /**
     * Update the saved payment info display with fetched data.
     * Renders a selectable list of saved entries (any payment type) plus a
     * "use a new …" option; selecting a saved entry hides the schema form,
     * choosing "use new" (or having no saved entries) shows it.
     *
     * @param {Array}  paymentInfoData - Array of saved payment information records.
     * @param {string} paymentType     - Method-level payment type (fallback for rendering).
     */
    updatePaymentInfoDisplay(paymentInfoData, paymentType) {
        const savedCardsSection = this.modal.querySelector('[data-saved-cards]');
        const cardsList         = this.modal.querySelector('[data-saved-cards-list]');
        const emptyNote         = this.modal.querySelector('[data-saved-cards-empty]');
        const loadingEl         = this.modal.querySelector('[data-saved-cards-loading]');

        if (!savedCardsSection) return;

        const titleEl     = this.modal.querySelector('[data-saved-cards-title]');
        const depositNote = this.modal.querySelector('[data-saved-cards-deposit-note]');

        savedCardsSection.style.display = 'block';
        if (loadingEl)   loadingEl.style.display   = 'none';
        if (titleEl)     titleEl.style.display     = '';   // reset (deposit-first state hides it)
        if (depositNote) depositNote.style.display = 'none';

        const entries = Array.isArray(paymentInfoData) ? paymentInfoData : [];

        // No saved entries → behaviour depends on whether the method has admin-defined
        // extra fields. Render the schema first so hasFields() reflects this method.
        if (entries.length === 0) {
            if (cardsList) { cardsList.style.display = 'none'; cardsList.innerHTML = ''; }
            this.showSchemaForNewEntry();
            const hasSchemaFields = !!(this.schemaRenderer && this.schemaRenderer.hasFields());

            if (hasSchemaFields) {
                // The method has fields → the schema form is the way to enter new
                // details; hide the saved-info section entirely.
                savedCardsSection.style.display = 'none';
                if (emptyNote) emptyNote.style.display = 'none';
            } else {
                // Nothing saved AND no fields to enter new details → the player must
                // make a deposit with this method before they can withdraw to it.
                if (titleEl)     titleEl.style.display     = 'none';
                if (emptyNote)   emptyNote.style.display   = 'none';
                if (depositNote) depositNote.style.display = 'block';
                this.setWithdrawalDisabled(true);
            }
            return;
        }

        if (cardsList) cardsList.style.display = 'flex';
        if (emptyNote) emptyNote.style.display = 'none';

        cardsList.innerHTML = '';
        entries.forEach((card, index) => {
            const id   = card.id || card.paymentInformationId || index;
            const info = this.formatSavedEntry(card, paymentType);
            cardsList.appendChild(this.buildSavedItem(id, info, index === 0));
        });

        // Trailing "use a new account / address" option — not for saved cards,
        // which are select-only (new cards are added through the deposit flow).
        if (paymentType !== 'CREDIT_CARD') {
            cardsList.appendChild(this.buildNewEntryItem());
        }

        // Default selection is the first saved entry → schema form stays hidden.
        this.hideSchemaForSavedEntry();
    }

    /**
     * Build a display row for a saved payment info record, keyed by its type.
     * Reads card.detailsAsMap; falls back to the method-level type when the
     * record carries none.
     *
     * @param {Object} card        - Saved payment info record.
     * @param {string} fallbackType - Method-level payment type.
     * @returns {{ primary: string, secondary: string, meta: string, icon: string }}
     */
    formatSavedEntry(card, fallbackType) {
        const map  = card.detailsAsMap || {};
        const type = (card.type || fallbackType || '').toUpperCase();
        const icon = {
            CREDIT_CARD:    'fa-credit-card',
            BANK:           'fa-building-columns',
            CRYPTO_ADDRESS: 'fa-coins',
            PAYMENT_WALLET: 'fa-wallet',
        }[type] || 'fa-wallet';

        if (type === 'CREDIT_CARD') {
            // CARD_NUMBER format: "401288******1881" — take last 4 chars.
            const cardNumber = map.CARD_NUMBER || '';
            const last4      = cardNumber ? cardNumber.slice(-4) : '****';
            const brand      = (map.CARD_TYPE || 'Card').toUpperCase();
            // EXPIRY_DATE format: "1230" → 12/30.
            const expiryRaw  = map.EXPIRY_DATE || '';
            const expiry     = expiryRaw.length === 4 ? `${expiryRaw.slice(0, 2)}/${expiryRaw.slice(2)}` : '';
            return { primary: brand, secondary: `•••• •••• •••• ${last4}`, meta: expiry ? `Exp. ${expiry}` : '', icon };
        }

        if (type === 'CRYPTO_ADDRESS') {
            const addr     = map.CRYPTO_ADDRESS || '';
            const currency = map.CRYPTO_ADDRESS_CURRENCY || '';
            const short    = addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
            // Coin and network side by side ("USDT · TRC20"): two saved addresses can differ
            // only in the network, and a wallet used on the wrong one does not receive the
            // funds. `primary` is not rendered by buildSavedItem(), so they go in `meta` —
            // escaped, as buildSavedItem() writes it as HTML.
            const network  = map.CRYPTO_ADDRESS_PROTOCOL || '';
            const meta     = [currency, network].filter(Boolean).map((part) => this.escapeText(part)).join(' · ');
            return { primary: currency || 'Crypto', secondary: short, meta, icon };
        }

        if (type === 'BANK') {
            const account  = map.ACCOUNT_NUMBER || '';
            const last4    = account ? account.slice(-4) : '';
            const payout   = map.PAYOUT_TYPE || '';
            const bankCode = map.BANK_CODE || '';
            return {
                primary:   payout ? `Bank (${payout})` : 'Bank Account',
                secondary: last4 ? `•••• ${last4}` : bankCode,
                meta:      last4 && bankCode ? bankCode : '',
                icon,
            };
        }

        // PAYMENT_WALLET / unknown — show the first meaningful detail value.
        const firstVal = Object.values(map).find(v => v) || '';
        return { primary: 'Wallet', secondary: firstVal, meta: '', icon };
    }

    /**
     * Escape a value for insertion into HTML.
     *
     * @param {string} text
     * @returns {string}
     */
    escapeText(text) {
        const el = document.createElement('span');
        el.textContent = String(text);
        return el.innerHTML;
    }

    /**
     * Build a selectable saved-entry row.
     *
     * @param {string|number} id      - paymentInformationId, submitted on withdrawal.
     * @param {Object}        info     - Row from formatSavedEntry().
     * @param {boolean}       checked  - Whether this row is selected by default.
     * @returns {HTMLLabelElement}
     */
    buildSavedItem(id, info, checked) {
        const item = document.createElement('label');
        item.className = 'wallet-withdrawal-saved-cards__item' + (checked ? ' is-selected' : '');
        item.setAttribute('for', `saved-card-${id}`);
        item.setAttribute('data-card-id', id);
        item.innerHTML = `
            <div class="wallet-withdrawal-saved-cards__radio-wrapper">
                <input type="radio" name="saved_card" id="saved-card-${id}" value="${id}" ${checked ? 'checked' : ''}>
                <label for="saved-card-${id}"></label>
            </div>
            <span class="wallet-withdrawal-saved-cards__card-info">
                ${info.secondary ? `<span class="wallet-withdrawal-saved-cards__card-number">${info.secondary}</span>` : ''}
            </span>
            <span class="wallet-withdrawal-saved-cards__card-right">
            
            
                ${info.meta ? `<span class="wallet-withdrawal-saved-cards__card-expiry">${info.meta}</span>` : ''}
                <span class="wallet-withdrawal-saved-cards__card-icon">
                    <i class="fa-solid ${info.icon}"></i>
                </span>
            </span>`;
        this._bindSavedRadio(item);
        return item;
    }

    /**
     * Build the trailing "use a new account / address" option. Its value is the
     * `__new__` sentinel, which the submit handler treats as no saved id.
     *
     * @returns {HTMLLabelElement}
     */
    buildNewEntryItem() {
        const item = document.createElement('label');
        item.className = 'wallet-withdrawal-saved-cards__item wallet-withdrawal-saved-cards__item--new';
        item.setAttribute('for', 'saved-card-new');
        item.innerHTML = `
            <div class="wallet-withdrawal-saved-cards__radio-wrapper">
                <input type="radio" name="saved_card" id="saved-card-new" value="__new__">
                <label for="saved-card-new"></label>
            </div>
            <span class="wallet-withdrawal-saved-cards__card-info">
                <span class="wallet-withdrawal-saved-cards__card-brand">Use a new account / address</span>
            </span>
            <span class="wallet-withdrawal-saved-cards__card-right">
                <span class="wallet-withdrawal-saved-cards__card-icon">
                    <i class="fa-solid fa-plus"></i>
                </span>
            </span>`;
        this._bindSavedRadio(item);
        return item;
    }

    /**
     * Wire a saved-entry radio: highlight the selected row and toggle the schema
     * form (hidden for a saved entry, shown for "use a new …").
     *
     * @param {HTMLElement} item
     */
    _bindSavedRadio(item) {
        const radio = item.querySelector('input[type="radio"]');
        const list  = this.modal.querySelector('[data-saved-cards-list]');
        if (!radio || !list) return;

        radio.addEventListener('change', () => {
            list.querySelectorAll('.wallet-withdrawal-saved-cards__item')
                .forEach(el => el.classList.remove('is-selected'));
            item.classList.add('is-selected');

            if (radio.value === '__new__') {
                this.showSchemaForNewEntry();
            } else {
                this.hideSchemaForSavedEntry();
            }
        });
    }

    /**
     * Reveal the schema-defined extra fields for the active method (manual entry).
     */
    showSchemaForNewEntry() {
        if (this.schemaRenderer && this.activeButton) {
            this.schemaRenderer.renderForMethod(this.activeButton);
        }
    }

    /**
     * Hide the schema form (a saved entry supplies the details via its id).
     * Passing null nulls the schema so hasFields()/collectValues() stay inert.
     */
    hideSchemaForSavedEntry() {
        if (this.schemaRenderer) {
            this.schemaRenderer.renderForMethod(null);
        }
    }

    /**
     * Show/hide the amount + submit sections. Used to block withdrawal when the
     * method has no saved details and no fields to enter new ones (deposit first).
     *
     * @param {boolean} disabled
     */
    setWithdrawalDisabled(disabled) {
        const amountSection = this.modal.querySelector('.wallet-withdrawal-amount-section');
        const submitSection = this.modal.querySelector('.wallet-withdrawal-submit-section');
        if (amountSection) amountSection.style.display = disabled ? 'none' : '';
        if (submitSection) submitSection.style.display = disabled ? 'none' : '';
    }
    
    /**
     * Update min/max limits based on the selected method button
     * @param {HTMLElement|null} button - Currently selected method button
     */
    updateLimitsFromButton(button) {
        if (!button) return;

        // A bound the method doesn't define stays NaN and is carried through as "no
        // limit". Substituting a placeholder here would silently override the range
        // configured in WP for this card.
        const minWithdrawal = parseFloat(button.getAttribute('data-min-withdrawal'));
        const maxWithdrawal = parseFloat(button.getAttribute('data-max-withdrawal'));

        // Update amount input min/max attributes (values already in correct currency)
        const amountInput = this.modal.querySelector('.wallet-withdrawal-amount-input');
        if (amountInput) {
            this.applyBoundAttribute(amountInput, 'Minimum', minWithdrawal);
            this.applyBoundAttribute(amountInput, 'Maximum', maxWithdrawal);
        }

        // Update displayed limits with currency formatting
        if (this.currencyDisplayManager) {
            this.currencyDisplayManager.updateMinMaxDisplay();
            this.currencyDisplayManager.updateWithdrawalMethodLimits();
        } else {
            // Fallback to simple display if no currency manager
            const minValueSpan = this.modal.querySelector('.wallet-withdrawal-min-value');
            const maxValueSpan = this.modal.querySelector('.wallet-withdrawal-max-value');

            if (minValueSpan) minValueSpan.textContent = isNaN(minWithdrawal) ? '' : minWithdrawal;
            if (maxValueSpan) maxValueSpan.textContent = isNaN(maxWithdrawal) ? '' : maxWithdrawal;
        }

        // Update validator limits if available
        if (window.withdrawalFlow && window.withdrawalFlow.getValidator()) {
            window.withdrawalFlow.getValidator().updateLimits(
                isNaN(minWithdrawal) ? null : minWithdrawal,
                isNaN(maxWithdrawal) ? null : maxWithdrawal
            );
        }
    }

    /**
     * Set or clear one bound attribute on the amount input.
     *
     * @param {HTMLInputElement} input
     * @param {string} attr - 'Minimum' or 'Maximum'
     * @param {number} value - NaN clears the attribute
     */
    applyBoundAttribute(input, attr, value) {
        if (isNaN(value)) {
            input.removeAttribute(attr);
        } else {
            input.setAttribute(attr, value);
        }
    }
    
    /**
     * Update active state for payment method buttons
     * @param {string} activeVariantKey - Variant key of the currently selected card
     */
    updateButtonStates(activeVariantKey) {
        const buttons = this.modal.querySelectorAll('.wallet-withdrawal-payment-method-item');

        buttons.forEach(button => {
            const variantKey = button.getAttribute('data-variant-key');

            if (variantKey === activeVariantKey) {
                button.classList.add('is-active');
            } else {
                button.classList.remove('is-active');
            }
        });
    }
    
    /**
     * Show the selected card's operator-configured legal notice (PSP trademark
     * attributions, e.g. Interac's) under the security badges, or hide the line
     * entirely for cards that have none.
     *
     * @param {HTMLElement|null} button - The selected method button.
     */
    updateLegalNotice(button) {
        const notice = this.modal.querySelector('.wallet-withdrawal-security-legal');
        if (!notice) return;

        const text = button ? (button.getAttribute('data-legal-text') || '') : '';

        // textContent, not innerHTML — the value is operator-entered plain text.
        notice.textContent   = text;
        notice.style.display = text ? '' : 'none';
    }

    /**
     * Toggle between card form and iframe based on payment method
     * @param {string} method - Payment method identifier
     */
    togglePaymentDisplay(method) {
        const cardForm = this.modal.querySelector('[data-payment-type="bank"]');
        const iframeContainer = this.modal.querySelector('[data-payment-type="iframe"]');
        const iframeTitle = this.modal.querySelector('.wallet-withdrawal-iframe-title');
        const amountSection = this.modal.querySelector('.wallet-withdrawal-amount-section');
        const submitSection = this.modal.querySelector('.wallet-withdrawal-submit-section');
        
        if (!cardForm || !iframeContainer) return;
        
        // Show card form for visa, hide for all others
        if (this.cardPaymentMethods.includes(method)) {
            cardForm.style.display = 'block';
            iframeContainer.style.display = 'none';
            if (amountSection) amountSection.style.display = 'block';
            if (submitSection) submitSection.style.display = 'flex';
        } else {
            cardForm.style.display = 'none';
            iframeContainer.style.display = 'block';
            if (amountSection) amountSection.style.display = 'none';
            if (submitSection) submitSection.style.display = 'none';
            
            // Update iframe title based on method
            if (iframeTitle) {
                iframeTitle.textContent = this.getPaymentMethodTitle(method);
            }
        }
    }
    
    /**
     * Get display title for payment method
     * @param {string} method - Payment method identifier
     * @returns {string} Display title
     */
    getPaymentMethodTitle(method) {
        const titles = {
            'bank': 'Bank Transfer',
            'mifinity': 'MiFinity',
            'crypto': 'Crypto'
        };
        
        return titles[method] || 'Payment Method';
    }
    
    /**
     * Get currently selected payment method
     * @returns {string} Selected method identifier
     */
    getSelectedMethod() {
        return this.selectedMethod;
    }
    
    /**
     * Load iframe for non-card payment methods
     * @param {string} iframeUrl - URL for the payment provider iframe
     */
    loadPaymentIframe(iframeUrl) {
        const iframeContainer = this.modal.querySelector('.wallet-withdrawal-iframe-container');
        const placeholder = this.modal.querySelector('.wallet-withdrawal-iframe-placeholder');
        
        if (!iframeContainer) return;
        
        // Remove placeholder
        if (placeholder) {
            placeholder.remove();
        }
        
        // Create and inject iframe
        const iframe = document.createElement('iframe');
        iframe.src = iframeUrl;
        iframe.setAttribute('allow', 'geolocation *;camera *;payment *;clipboard-read *;clipboard-write *;autoplay *;microphone *;fullscreen *;accelerometer *;magnetometer *;gyroscope *;picture-in-picture *;otp-credentials *;');
        iframe.setAttribute('data-hj-allow-iframe', 'true');
        iframe.style.border = 'none';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        
        iframeContainer.appendChild(iframe);
    }
    
    /**
     * Clear iframe content
     */
    clearPaymentIframe() {
        const iframeContainer = this.modal.querySelector('.wallet-withdrawal-iframe-container');
        if (!iframeContainer) return;
        
        // Remove existing iframe
        const existingIframe = iframeContainer.querySelector('iframe');
        if (existingIframe) {
            existingIframe.remove();
        }
        
        // Restore placeholder if not exists
        if (!iframeContainer.querySelector('.wallet-withdrawal-iframe-placeholder')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'wallet-withdrawal-iframe-placeholder';
            placeholder.innerHTML = `
                <i class="fa fa-credit-card" aria-hidden="true"></i>
                <p>Payment provider will load here</p>
            `;
            iframeContainer.appendChild(placeholder);
        }
    }
    
    /**
     * Get currently selected payment method
     * @returns {string|null}
     */
    getSelectedMethod() {
        return this.selectedMethod;
    }
}
